import React, { useState, useEffect } from 'react';
import { NavigationMenu } from '../components/NavigationMenu';
import { Button } from '../components/Button';
import { LoadingAnimation } from '../components/LoadingAnimation';
import { useGeneration } from '../context/GenerationContext';
import { useAuth } from '../context/AuthContext';
import { useCustomNavigate } from '../hooks/useCustomNavigate';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { API_URL } from '../config/api';
import { Target, ArrowRight, Calendar, Sliders } from 'lucide-react';
import { roadmapApi } from '../services/api';
import { tasksApi } from '../services/api';
import { localStorageService } from '../services/localStorageService';
import { PageHeader } from '../components/PageHeader';
import { useGenerationStatus } from '../hooks/useGenerationStatus';
import { GenerationLimitsStatus } from '../components/GenerationLimitsStatus';

// Интерфейс для типизации черновика
interface RoadmapDraft {
  goal: string;
  deadline: string;
  detailLevel: number;
}

function Roadmap() {
  useDocumentTitle('Roadmap');
  const [goal, setGoal] = useState('');
  const [deadline, setDeadline] = useState('');
  const [detailLevel, setDetailLevel] = useState(5);
  const { startGeneration, canAddTask } = useGeneration();
  const navigate = useCustomNavigate();
  const { token, hasActiveSubscription } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);
  const [taskStartTime, setTaskStartTime] = useState<number | null>(null);
  const [taskStatusCheckCount, setTaskStatusCheckCount] = useState(0);
  const { status: generationStatus, loading: isLimitsLoading, error: limitsError, refetch: refetchGenerationStatus } = useGenerationStatus('Roadmap');

  // Загрузка черновика при монтировании компонента
  useEffect(() => {
    const savedDraft = localStorageService.getRoadmapDraft<RoadmapDraft>();
    if (savedDraft) {
      // Используем проверки на наличие данных для предотвращения установки пустых значений
      if (savedDraft.goal) setGoal(savedDraft.goal);
      if (savedDraft.deadline) setDeadline(savedDraft.deadline);
      if (savedDraft.detailLevel !== undefined) setDetailLevel(savedDraft.detailLevel);
    }
  }, []);

  // Сохранение черновика при изменении состояний
  useEffect(() => {
    // Используем debounce для сохранения черновика, чтобы не перегружать localStorage
    const saveTimeout = setTimeout(() => {
      const draftData: RoadmapDraft = {
        goal,
        deadline,
        detailLevel
      };
      
      localStorageService.saveRoadmapDraft(draftData);
    }, 500); // задержка в 500 мс для дебаунсинга
    
    return () => {
      clearTimeout(saveTimeout);
    };
  }, [goal, deadline, detailLevel]);

  // Функция для очистки черновика после успешного старта задачи
  const clearRoadmapDraft = () => {
    localStorageService.clearRoadmapDraft();
  };

  // Отладка: добавим вывод значения currentTaskId при любом изменении
  useEffect(() => {
    console.log('[Debug] Значение currentTaskId в Roadmap изменилось:', currentTaskId);
  }, [currentTaskId]);

  // Функция для проверки статуса задачи
  useEffect(() => {
    if (!currentTaskId) return;

    console.log(`[Debug] Отслеживание начато для задачи: ${currentTaskId}`);

    // Не проверяем статус временных задач
    if (currentTaskId.startsWith('temp-')) {
      console.log(`[Debug] Пропускаем проверку статуса для временного ID: ${currentTaskId}`);
      return;
    }

    // Устанавливаем время начала задачи
    if (!taskStartTime) {
      setTaskStartTime(Date.now());
    }

    const checkTaskStatus = async () => {
      try {
        console.log(`[Debug] Проверка статуса задачи: ${currentTaskId}`);
        
        // Увеличиваем счетчик проверок
        setTaskStatusCheckCount(prev => prev + 1);
        
        // Проверяем, не слишком ли долго выполняется задача (более 15 минут)
        if (taskStartTime && (Date.now() - taskStartTime > 15 * 60 * 1000)) {
          console.log(`[Debug] Задача ${currentTaskId} выполняется более 15 минут. Перенаправляем на главную страницу.`);
          // Не показываем ошибку, просто перенаправляем на главную
          navigate('/');
          setCurrentTaskId(null);
          setTaskStartTime(null);
          setTaskStatusCheckCount(0);
          setIsLoading(false);
          return;
        }
        
        // Проверяем, не слишком ли много проверок без изменения статуса (более 100)
        if (taskStatusCheckCount > 100) {
          console.log(`[Debug] Слишком много проверок статуса для задачи ${currentTaskId}. Прекращаем отслеживание.`);
          setCurrentTaskId(null);
          setTaskStartTime(null);
          setTaskStatusCheckCount(0);
          setIsLoading(false);
          return;
        }
        
        // Используем метод tasksApi.getTaskResult для проверки статуса
        const response = await tasksApi.getTaskResult(currentTaskId);
        const task = response.data;
        
        console.log(`[Debug] Получен статус задачи: ${task.status}`);
        
        // Проверяем, завершена ли задача
        if (task.status === 'Successfull' || task.status === 'Successful') {
          // Дополнительно проверяем наличие данных результата
          if (task.data) {
            console.log('[Debug] Данные задачи получены:', task.data);
            
            const redirectUrl = `/roadmap/result/${currentTaskId}`;
            console.log(`[Debug] Задача завершена, перенаправление на: ${redirectUrl}`);
            
            // Очищаем текущую задачу перед перенаправлением
            setCurrentTaskId(null);
            setTaskStartTime(null);
            setTaskStatusCheckCount(0);
            setIsLoading(false);
            
            // Добавляем задержку перед перенаправлением, чтобы данные точно успели обработаться
            setTimeout(() => {
              try {
                console.log(`[Debug] Выполняем перенаправление на: ${redirectUrl}`);
                
                // Сохраняем ID задачи в sessionStorage для гарантированного доступа на странице результатов
                sessionStorage.setItem('lastRoadmapTaskId', currentTaskId || '');
                
                // Используем прямое перенаправление
                window.location.href = `${window.location.origin}${redirectUrl}`;
              } catch (err) {
                console.error('[Debug] Ошибка при перенаправлении:', err);
                
                // Запасной вариант - просто открыть новую вкладку
                window.open(`${window.location.origin}${redirectUrl}`, '_blank');
              }
            }, 500);
          } else {
            console.error('[Debug] Задача завершена, но данные пустые');
            
            // Очищаем текущую задачу
            setCurrentTaskId(null);
            setTaskStartTime(null);
            setTaskStatusCheckCount(0);
            setIsLoading(false);
          }
        } else if (task.status === 'Error' || task.status === 'Cancelled') {
          // Если ошибка или отмена, тоже очищаем задачу
          console.log(`[Debug] Задача не выполнена (статус: ${task.status}), прекращаем отслеживание`);
          
          if (task.status === 'Error') {
          }
          
          setCurrentTaskId(null);
          setTaskStartTime(null);
          setTaskStatusCheckCount(0);
          setIsLoading(false);
        } else {
          console.log(`[Debug] Задача еще выполняется (статус: ${task.status}), продолжаем отслеживание`);
        }
      } catch (error) {
        console.error('[Debug] Ошибка при проверке статуса задачи:', error);
        
        // Если это 404 ошибка (задача не найдена), возможно задача еще создается
        if (error && typeof error === 'object' && 'response' in error) {
          const response = (error as any).response;
          if (response?.status === 404) {
            console.log(`[Debug] Задача ${currentTaskId} еще не найдена на сервере, продолжаем ожидание...`);
            return; // Продолжаем polling
          }
        }
        
        // Если произошло слишком много ошибок проверки, останавливаем отслеживание
        if (taskStatusCheckCount > 20) {
          console.log('[Debug] Слишком много ошибок при проверке статуса. Прекращаем отслеживание.');
          setCurrentTaskId(null);
          setTaskStartTime(null);
          setTaskStatusCheckCount(0);
          setIsLoading(false);
        }
      }
    };

    // Проверяем статус каждые 3 секунды
    const intervalId = setInterval(checkTaskStatus, 3000);
    console.log(`[Debug] Установлен интервал ${intervalId} для проверки статуса`);

    // Очищаем интервал при размонтировании компонента
    return () => {
      console.log(`[Debug] Очистка интервала ${intervalId}`);
      clearInterval(intervalId);
    };
  }, [currentTaskId, navigate, taskStartTime, taskStatusCheckCount]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      navigate('/auth/login');
      return;
    }

    if (generationStatus && !generationStatus.canGenerate) {
      return;
    }

    // Проверяем наличие активной подписки
    if (!hasActiveSubscription) {
      navigate('/tariffs');
      return;
    }

    if (!goal.trim()) {
      return;
    }

    if (!deadline) {
      return;
    }

    if (!canAddTask) {
      return;
    }

    // Показываем анимацию сразу
    setIsLoading(true);
    
    // Очищаем черновик сразу
    clearRoadmapDraft();

    try {
      console.log('[Debug] Отправка запроса на создание roadmap');
      
      // Отправляем запрос на сервер (не ждем ответа)
      roadmapApi.generate({
        goal,
        deadline,
        detailLevel
      }).then((response) => {
        const data = response.data;
        console.log('[Debug] Полный ответ от сервера:', data);
        
        // Поиск идентификатора задачи в ответе
        const realTaskId = data.taskId || data.id || (data.task && data.task.id);
        
        if (realTaskId) {
          console.log(`[Debug] Получен реальный taskId: ${realTaskId}`);
          // Задача будет автоматически добавлена через SignalR уведомления от бэкенда
          // startGeneration убран, чтобы избежать дублирования
        }
      }).catch((error) => {
        console.error('Error generating roadmap:', error);
        // Ошибки обрабатываются в фоне, не влияют на UI
      });
      
      // Показываем анимацию 4 секунды, затем переходим на страницу задач
      setTimeout(() => {
        setIsLoading(false);
        navigate('/tasks');
      }, 4000);
      
    } catch (error) {
      console.error('Error starting roadmap generation:', error);
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800">
        <div className="hidden md:block">
          <NavigationMenu />
        </div>
        <div className="flex flex-col items-center justify-center h-[calc(100vh-96px)]">
          <div className="scale-125 transform">
            <LoadingAnimation size="large" text="Создание дорожной карты..." />
          </div>
          <div className="mt-16 max-w-2xl text-center">
            <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300">
              Создание дорожной карты может занять некоторое время.
              <br /><br />
              Ваша задача обрабатывается в фоновом режиме. Вы можете перейти на другую страницу, а результат будет доступен в разделе "Задачи".
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-slate-800 dark:to-gray-900 transition-colors duration-200 mobile-content-padding overflow-x-hidden">
      <div className="hidden md:block">
        <NavigationMenu />
      </div>
      <div className="w-full max-w-7xl mx-auto px-4 py-8 lg:py-12">
        <PageHeader
          title="Дорожная карта (Roadmap)"
          subtitle="Укажите вашу цель и получите детальный план действий"
        />

        <div className="w-full max-w-3xl mx-auto">
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:border-gray-700/50 p-4 sm:p-6 md:p-8 overflow-hidden">
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                  <Target className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  Ваша цель
                </label>
                <textarea
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                  className="w-full p-2 sm:p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 transition-colors text-sm sm:text-base"
                  rows={4}
                  placeholder="Опишите, чего вы хотите достичь..."
                  required
                />
              </div>
              
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                  <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  Дедлайн
                </label>
                <input
                  type="date"
                  value={deadline}
                  onChange={(e) => setDeadline(e.target.value)}
                  className="w-full p-2 sm:p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 transition-colors text-sm sm:text-base"
                  required
                />
              </div>
              
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                  <Sliders className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  Уровень детализации
                </label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={detailLevel}
                  onChange={(e) => setDetailLevel(Number(e.target.value))}
                  className="w-full accent-blue-600 h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                  <span>Базовый</span>
                  <span>Средний</span>
                  <span>Детальный</span>
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Уровень {detailLevel}: {detailLevel <= 3 ? 'минимум деталей, только ключевые этапы' : 
                  detailLevel <= 7 ? 'оптимальный уровень детализации' : 'максимально подробный план'}
                </p>
              </div>
              
              <GenerationLimitsStatus 
                className="mb-4" 
                generationType="Roadmap" 
                status={generationStatus}
                loading={isLimitsLoading}
                error={limitsError}
              />
              
              <Button
                type="submit"
                disabled={!generationStatus?.canGenerate}
                variant="primary"
                fullWidth
                className="flex items-center justify-center py-3 sm:py-3 px-4 sm:px-6 font-medium text-base sm:text-lg shadow-md hover:shadow-lg transform"
              >
                <span>Создать Roadmap</span>
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Roadmap;
